import { useEffect, useCallback } from 'react';
import { useProjectStore } from '@/store/projectStore';

export const useKeyboardShortcuts = () => {
  const setIsPlaying = useProjectStore((state) => state.setIsPlaying);
  const isPlaying = useProjectStore((state) => state.playerState.isPlaying);
  const deleteSelectedOverlays = useProjectStore((state) => state.deleteSelectedOverlays);
  const selectedIds = useProjectStore((state) => state.selectionState.selectedIds);
  const overlays = useProjectStore((state) => state.project.overlays);
  const undo = useProjectStore((state) => state.undo);
  const redo = useProjectStore((state) => state.redo);
  const selectAll = useProjectStore((state) => state.selectAll);
  const deselectAll = useProjectStore((state) => state.deselectAll);
  const setSelectedTool = useProjectStore((state) => state.setSelectedTool);
  const duplicateOverlay = useProjectStore((state) => state.duplicateOverlay);
  const updateOverlay = useProjectStore((state) => state.updateOverlay);
  const reorderOverlay = useProjectStore((state) => state.reorderOverlay);
  const pushHistory = useProjectStore((state) => state.pushHistory);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Ignore if typing in an input
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

    // Space - Play/Pause
    if (e.code === 'Space') {
      e.preventDefault();
      setIsPlaying(!isPlaying);
      return;
    }

    // Delete/Backspace - Delete selected
    if (e.code === 'Delete' || e.code === 'Backspace') {
      e.preventDefault();
      deleteSelectedOverlays();
      return;
    }

    // Ctrl/Cmd + Z - Undo
    if (ctrlKey && e.code === 'KeyZ' && !e.shiftKey) {
      e.preventDefault();
      undo();
      return;
    }

    // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y - Redo
    if ((ctrlKey && e.code === 'KeyZ' && e.shiftKey) || (ctrlKey && e.code === 'KeyY')) {
      e.preventDefault();
      redo();
      return;
    }

    // Ctrl/Cmd + A - Select all
    if (ctrlKey && e.code === 'KeyA') {
      e.preventDefault();
      selectAll();
      return;
    }

    // Ctrl/Cmd + D - Duplicate
    if (ctrlKey && e.code === 'KeyD') {
      e.preventDefault();
      if (selectedIds.length === 1) {
        duplicateOverlay(selectedIds[0]);
      }
      return;
    }

    // Ctrl/Cmd + G - Group (select all selected) — placeholder for future group feature
    // Currently selects all as a convenience
    if (ctrlKey && e.code === 'KeyG') {
      e.preventDefault();
      // Future: group selected overlays
      return;
    }

    // Ctrl/Cmd + L - Lock/Unlock selected overlay
    if (ctrlKey && e.code === 'KeyL') {
      e.preventDefault();
      if (selectedIds.length > 0) {
        selectedIds.forEach((id) => {
          const overlay = overlays.find((o) => o.id === id);
          if (overlay) {
            updateOverlay(id, { locked: !overlay.locked });
          }
        });
        pushHistory('toggle lock');
      }
      return;
    }

    // Ctrl/Cmd + ] - Bring forward
    if (ctrlKey && e.code === 'BracketRight') {
      e.preventDefault();
      if (selectedIds.length === 1) {
        reorderOverlay(selectedIds[0], 'up');
        pushHistory('bring forward');
      }
      return;
    }

    // Ctrl/Cmd + [ - Send backward
    if (ctrlKey && e.code === 'BracketLeft') {
      e.preventDefault();
      if (selectedIds.length === 1) {
        reorderOverlay(selectedIds[0], 'down');
        pushHistory('send backward');
      }
      return;
    }

    // Ctrl/Cmd + Shift + ] - Bring to front
    if (ctrlKey && e.shiftKey && e.code === 'BracketRight') {
      e.preventDefault();
      if (selectedIds.length === 1) {
        reorderOverlay(selectedIds[0], 'top');
        pushHistory('bring to front');
      }
      return;
    }

    // Ctrl/Cmd + Shift + [ - Send to back
    if (ctrlKey && e.shiftKey && e.code === 'BracketLeft') {
      e.preventDefault();
      if (selectedIds.length === 1) {
        reorderOverlay(selectedIds[0], 'bottom');
        pushHistory('send to back');
      }
      return;
    }

    // Arrow keys - Nudge selected overlays (1px normal, 10px with Shift)
    if (
      e.code === 'ArrowUp' ||
      e.code === 'ArrowDown' ||
      e.code === 'ArrowLeft' ||
      e.code === 'ArrowRight'
    ) {
      if (selectedIds.length === 0) return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const dx =
        e.code === 'ArrowLeft' ? -step : e.code === 'ArrowRight' ? step : 0;
      const dy =
        e.code === 'ArrowUp' ? -step : e.code === 'ArrowDown' ? step : 0;

      selectedIds.forEach((id) => {
        const overlay = overlays.find((o) => o.id === id);
        if (overlay && !overlay.locked) {
          updateOverlay(id, {
            bounds: {
              ...overlay.bounds,
              x: overlay.bounds.x + dx,
              y: overlay.bounds.y + dy,
            },
          });
        }
      });
      pushHistory('nudge overlay');
      return;
    }

    // Escape - Deselect all / Switch to select tool
    if (e.code === 'Escape') {
      e.preventDefault();
      deselectAll();
      setSelectedTool('select');
      return;
    }

    // Tool shortcuts (only when no modifier key is pressed)
    if (!ctrlKey && !e.altKey) {
      if (e.code === 'KeyV') {
        setSelectedTool('select');
        return;
      }
      if (e.code === 'KeyR') {
        setSelectedTool('rectangle');
        return;
      }
      if (e.code === 'KeyC') {
        setSelectedTool('circle');
        return;
      }
      if (e.code === 'KeyT') {
        setSelectedTool('text');
        return;
      }
      if (e.code === 'KeyB') {
        setSelectedTool('blur');
        return;
      }
      if (e.code === 'KeyP') {
        setSelectedTool('polygon');
        return;
      }
    }
  }, [
    isPlaying,
    selectedIds,
    overlays,
    setIsPlaying,
    deleteSelectedOverlays,
    undo,
    redo,
    selectAll,
    deselectAll,
    setSelectedTool,
    duplicateOverlay,
    updateOverlay,
    reorderOverlay,
    pushHistory,
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};
